import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UploadCloud, LayoutTemplate, RefreshCw, Trash2, Eye, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const SHOPIFY_FIELDS = ['title', 'body_html', 'vendor', 'product_type', 'tags', 'handle', 'image_url', 'images'];
const INVENTORY_FIELDS = ['product_title', 'variant_title', 'sku', 'vendor', 'category', 'image_url', 'price', 'selling_price'];
const SOURCES = [['shopify', 'Shopify field'], ['metafield', 'Shopify metafield'], ['inventory', 'Inventory field'], ['static', 'Static value'], ['skip', 'Skip']];
const STATUS_BADGE = {
  accepted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  content_issues: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  live: 'bg-green-500/20 text-green-400 border-green-500/30',
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function NoonPublishPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  // template editor state
  const [tplName, setTplName] = useState('');
  const [catSearch, setCatSearch] = useState('');
  const [catResults, setCatResults] = useState([]);
  const [category, setCategory] = useState('');
  const [attrs, setAttrs] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const [autoAr, setAutoAr] = useState(false);
  // publish state
  const [skusInput, setSkusInput] = useState('');
  const [tplId, setTplId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/noon/content/templates`, auth()),
        axios.get(`${API}/noon/content/products`, auth()),
      ]);
      setTemplates(t.data.templates || []);
      setProducts(p.data.products || []);
    } catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const [searchingCats, setSearchingCats] = useState(false);
  const searchCategories = async () => {
    setSearchingCats(true);
    try {
      const r = await axios.get(`${API}/noon/content/categories?search=${encodeURIComponent(catSearch)}`, auth());
      setCatResults(r.data.categories || []);
      if (!(r.data.categories || []).length) toast.info('No categories matched');
    } catch (e) { toast.error(e.response?.data?.detail || 'Category search failed'); }
    finally { setSearchingCats(false); }
  };

  const loadAttrs = async (code) => {
    setCategory(code); setLoadingAttrs(true); setAttrs([]); setMappings({});
    try {
      const r = await axios.get(`${API}/noon/content/categories/${encodeURIComponent(code)}/attributes`, auth());
      const list = r.data.attributes || [];
      list.sort((a, b) => (b.is_mandatory === true) - (a.is_mandatory === true));
      setAttrs(list);
      const init = {
        __brand__: { source: 'shopify', value: 'vendor' },
        __images__: { source: 'shopify', value: 'images' },
      };
      list.forEach(a => {
        const c = a.attribute_code;
        if (c === 'product_title') init[c] = { source: 'shopify', value: 'title' };
        else if (c === 'long_description' || c === 'description') init[c] = { source: 'shopify', value: 'body_html' };
        else init[c] = { source: a.is_mandatory ? 'static' : 'skip', value: '' };
      });
      setMappings(init);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load attributes'); }
    finally { setLoadingAttrs(false); }
  };

  const setMap = (code, patch) => setMappings(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }));

  const saveTemplate = async () => {
    if (!tplName.trim() || !category) { toast.error('Template name and category are required'); return; }
    const attrByCode = Object.fromEntries(attrs.map(a => [a.attribute_code, a]));
    const emptyRequired = Object.entries(mappings)
      .filter(([code, m]) => (attrByCode[code]?.is_mandatory) && m.source === 'static' && !String(m.value || '').trim())
      .map(([code]) => code);
    if (emptyRequired.length && !window.confirm(`Required attributes have empty static values: ${emptyRequired.join(', ')}. Save anyway? (submissions will be blocked until filled)`)) return;
    const mappingList = Object.entries(mappings)
      .filter(([, m]) => m.source !== 'skip' && (String(m.value || '').trim() || m.source !== 'static'))
      .map(([code, m]) => {
        const a = attrByCode[code] || {};
        return {
          attr_code: code, source: m.source, value: m.value, unit: m.unit || null,
          is_mandatory: !!a.is_mandatory, is_localizable: !!a.is_localizable, is_multivalued: !!a.is_multivalued,
        };
      });
    try {
      await axios.post(`${API}/noon/content/templates`, { name: tplName.trim(), category_code: category, mappings: mappingList, auto_translate_ar: autoAr }, auth());
      toast.success('Template saved');
      setTplName(''); setCategory(''); setAttrs([]); setMappings({}); setCatResults([]); setAutoAr(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save template'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await axios.delete(`${API}/noon/content/templates/${id}`, auth()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const doPreview = async () => {
    const sku = skusInput.split(',')[0]?.trim();
    if (!sku || !tplId) { toast.error('Enter a SKU and pick a template'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/noon/content/preview`, { sku, template_id: tplId }, auth());
      setPreviewData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const doSubmit = async () => {
    const skus = skusInput.split(',').map(s => s.trim()).filter(Boolean);
    if (!skus.length || !tplId) { toast.error('Enter SKU(s) and pick a template'); return; }
    if (!window.confirm(`Submit ${skus.length} product(s) to noon's catalog? They will go through noon QC before going live.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/noon/content/submit`, { skus, template_id: tplId }, auth());
      const ok = (r.data.results || []).filter(x => x.ok).length;
      const bad = (r.data.results || []).filter(x => !x.ok);
      if (ok) toast.success(`${ok} product(s) submitted to noon`);
      bad.forEach(b => toast.error(`${b.sku}: ${b.error}`));
      setPreviewData(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  const refreshProduct = async (id) => {
    try {
      await axios.post(`${API}/noon/content/products/${id}/refresh`, {}, auth());
      toast.success('Status refreshed from noon');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
  };

  const sourceValueInput = (code, m) => {
    if (m.source === 'shopify' || m.source === 'inventory') {
      const opts = m.source === 'shopify' ? SHOPIFY_FIELDS : INVENTORY_FIELDS;
      return (
        <Select value={m.value} onValueChange={(v) => setMap(code, { value: v })}>
          <SelectTrigger className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-xs" data-testid={`map-value-select-${code}`}><SelectValue placeholder="Field" /></SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">{opts.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (m.source === 'skip') return null;
    return <Input className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-xs"
      placeholder={m.source === 'metafield' ? 'namespace.key (e.g. custom.feature_1)' : 'Static value'}
      value={m.value} onChange={(e) => setMap(code, { value: e.target.value })} data-testid={`map-value-input-${code}`} />;
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="noon-publish-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings/noon')} className="text-wt-text-muted" data-testid="publish-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20"><UploadCloud className="h-6 w-6 text-yellow-400" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">List Products on noon</h1>
          <p className="text-sm text-wt-text-secondary">Attribute templates map Shopify data & metafields to noon attributes</p>
        </div>
      </div>

      {/* Template builder */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-yellow-400" /> Attribute Templates</h2>
        {templates.length > 0 && (
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="flex items-center justify-between gap-2 p-2 bg-wt-bg rounded-lg border border-wt-border" data-testid={`template-row-${t.name}`}>
                <p className="text-sm text-wt-text-primary"><b>{t.name}</b> <span className="text-wt-text-muted text-xs">· {t.category_code} · {(t.mappings || []).length} mappings</span>{t.auto_translate_ar && <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 text-[10px]" data-testid={`ar-badge-${t.name}`}>AR AI</Badge>}</p>
                <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.name}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div>
            <Label className="text-xs text-wt-text-muted">New template name</Label>
            <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Chairs" className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-xs text-wt-text-muted">Search noon category</Label>
            <div className="flex gap-2">
              <Input value={catSearch} onChange={(e) => setCatSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchCategories()} placeholder="e.g. chair, sofa" className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="category-search-input" />
              <Button variant="outline" className="border-wt-border text-wt-text-primary shrink-0" onClick={searchCategories} disabled={searchingCats} data-testid="category-search-btn">{searchingCats ? 'Searching…' : 'Search'}</Button>
            </div>
          </div>
          {catResults.length > 0 && (
            <Select value={category} onValueChange={loadAttrs}>
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="category-select"><SelectValue placeholder={`Pick category (${catResults.length})`} /></SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border max-h-72">{catResults.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>

        {loadingAttrs && <p className="text-wt-text-muted text-sm">Loading noon attributes…</p>}
        {attrs.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="attribute-mapping-list">
            {[{ attribute_code: '__brand__', label: 'Brand (top-level)', is_mandatory: true },
              { attribute_code: '__images__', label: 'Images (top-level)', is_mandatory: true },
              ...attrs].map(a => {
              const code = a.attribute_code;
              const m = mappings[code] || { source: 'skip', value: '' };
              return (
                <div key={code} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center p-2 bg-wt-bg rounded-lg border border-wt-border" data-testid={`attr-row-${code}`}>
                  <div className="text-xs text-wt-text-primary">
                    {a.label || code}
                    {a.is_mandatory && <Badge className="ml-1 bg-red-500/20 text-red-400 border border-red-500/30 text-[10px]">required</Badge>}
                    {a.is_multivalued && <Badge className="ml-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[10px]">multi</Badge>}
                    {(a.attribute_metric_units || []).length > 0 && <Badge className="ml-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 text-[10px]">metric</Badge>}
                  </div>
                  <Select value={m.source} onValueChange={(v) => setMap(code, { source: v, value: '' })}>
                    <SelectTrigger className="h-8 bg-wt-surface border-wt-border text-wt-text-primary text-xs" data-testid={`map-source-${code}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">{SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    {sourceValueInput(code, m)}
                    {(a.attribute_metric_units || []).length > 0 && m.source !== 'skip' && (
                      <Select value={m.unit || ''} onValueChange={(v) => setMap(code, { unit: v })}>
                        <SelectTrigger className="h-8 w-28 bg-wt-surface border-wt-border text-wt-text-primary text-xs" data-testid={`map-unit-${code}`}><SelectValue placeholder="unit" /></SelectTrigger>
                        <SelectContent className="bg-wt-paper border-wt-border">{a.attribute_metric_units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {attrs.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap">
            <Button onClick={saveTemplate} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="save-template-btn">Save Template</Button>
            <label className="flex items-center gap-2 cursor-pointer text-xs text-wt-text-secondary">
              <input type="checkbox" checked={autoAr} onChange={(e) => setAutoAr(e.target.checked)} className="h-4 w-4 accent-yellow-400" data-testid="auto-translate-ar-checkbox" />
              <Languages className="h-4 w-4 text-yellow-400" /> Auto-translate to Arabic with AI (titles, descriptions & text attributes get Arabic content)
            </label>
          </div>
        )}
      </div>

      {/* Publish */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-3">
        <h2 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2"><UploadCloud className="h-5 w-5 text-yellow-400" /> Publish to noon</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs text-wt-text-muted">SKU(s) — comma separated</Label>
            <Input value={skusInput} onChange={(e) => setSkusInput(e.target.value)} placeholder="SKU-001, SKU-002" className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="publish-skus-input" />
          </div>
          <Select value={tplId} onValueChange={setTplId}>
            <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="publish-template-select"><SelectValue placeholder="Template" /></SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border">{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-wt-border text-wt-text-primary" onClick={doPreview} disabled={busy} data-testid="preview-btn"><Eye className="h-4 w-4 mr-1" /> Preview First SKU</Button>
          <Button className="bg-wt-gold hover:bg-wt-gold-hover text-black" onClick={doSubmit} disabled={busy} data-testid="submit-to-noon-btn">{busy ? 'Working…' : 'Submit to noon'}</Button>
        </div>
        {previewData && (
          <div className="p-3 bg-wt-bg rounded-lg border border-wt-border text-xs space-y-1" data-testid="preview-result">
            {previewData.shopify_linked === false && (
              <p className="text-amber-400" data-testid="not-shopify-linked-warning">This SKU has no linked Shopify product — Shopify-sourced fields fall back to inventory data where possible.</p>
            )}
            {previewData.missing_mandatory?.length > 0 && (
              <p className="text-red-400">Missing required: {previewData.missing_mandatory.join(', ')}</p>
            )}
            {Object.entries(previewData.resolved || {}).map(([k, v]) => (
              <p key={k} className="text-wt-text-muted truncate"><span className="text-wt-text-primary">{k}</span>: {Array.isArray(v) ? v.join(', ') : String(v).slice(0, 120)}</p>
            ))}
            {previewData.translation_error && (
              <p className="text-amber-400" data-testid="translation-error">Arabic translation failed ({previewData.translation_error}) — product would be submitted with English content only.</p>
            )}
            {Object.keys(previewData.arabic || {}).length > 0 && (
              <div className="pt-2 mt-1 border-t border-wt-border space-y-1" data-testid="arabic-preview">
                <p className="text-yellow-400 font-medium flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> Arabic (AI-translated):</p>
                {Object.entries(previewData.arabic).map(([k, v]) => (
                  <p key={k} dir="rtl" className="text-wt-text-muted truncate text-right"><span className="text-wt-text-primary">{k}</span>: {Array.isArray(v) ? v.join('، ') : String(v).slice(0, 120)}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submissions */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-3">
        <h2 className="text-lg font-semibold text-wt-text-primary">noon Submissions</h2>
        {products.length === 0 ? (
          <p className="text-wt-text-muted text-sm text-center py-4" data-testid="no-submissions">Nothing submitted to noon yet</p>
        ) : products.map(p => (
          <div key={p.id} className="flex items-center justify-between gap-2 p-3 bg-wt-bg rounded-lg border border-wt-border flex-wrap" data-testid={`submission-row-${p.partner_sku}`}>
            <div className="min-w-0">
              <p className="text-sm text-wt-text-primary"><b>{p.partner_sku}</b> <span className="text-wt-text-muted text-xs">· {p.category_code} · parent {p.sku_parent || '—'}</span></p>
              {p.status_message && <p className="text-xs text-amber-400 truncate max-w-xl">{p.status_message}</p>}
              {p.completeness != null && <p className="text-xs text-wt-text-muted">Content completeness: {p.completeness}%</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`${STATUS_BADGE[p.status] || STATUS_BADGE.accepted} border`}>{p.status === 'accepted' ? 'Submitted — QC pending' : p.status.replace('_', ' ')}</Badge>
              <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary" onClick={() => refreshProduct(p.id)} data-testid={`refresh-submission-${p.partner_sku}`}><RefreshCw className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
